/**
 * 2D histogram. Bins two numeric columns (`encoding.xKey`, `encoding.yKey`)
 * into a density heatmap. Single trace, no series or grouping.
 */

import type { ChartTypeDefinition, ChartTrace } from '../types';
import { registerChart } from '../registry';
import { xColumn } from '../shared/encoding';
import { toFiniteNumber } from '../shared/number';

export const histogram2dChart: ChartTypeDefinition = {
  id: 'histogram2d',
  label: '2D Histogram',
  // A density heatmap reads best near-square so neither axis is squashed.
  defaultAspectRatio: 1.1,
  capabilities: {
    multiSeries: false,
    supportsXKey: true,
    supportsYKey: true,
    supportsGroupBy: false,
    supportsLimits: false,
    filterable: true,
    supportsStacked: false,
    legendByDefault: false,
  },
  buildTraces(model) {
    const { data, encoding } = model;
    const yKey = encoding.yKey ?? 'y';

    // Resolve the x channel once; absent xKey falls back to the row index.
    const xValues = xColumn(data, encoding.xKey);

    // Bin only rows where both coordinates are real numbers; a blank/null cell
    // would otherwise be coerced to 0 and pile up a phantom bin at the origin.
    const idx = data
      .map((_, i) => i)
      .filter((i) => toFiniteNumber(xValues[i]) !== null && toFiniteNumber(data[i][yKey]) !== null);

    const trace: ChartTrace = {
      x: idx.map((i) => toFiniteNumber(xValues[i])),
      y: idx.map((i) => toFiniteNumber(data[i][yKey])),
      type: 'histogram2d',
      colorscale: 'YlOrRd',
    };
    return [trace];
  },
};

registerChart(histogram2dChart);
